import { Helmet } from "react-helmet-async";
import { siteConfig } from "@/config/website.config";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: "website" | "article";
  noIndex?: boolean;
  location?: string; // For location-specific pages
  jsonLd?: object | object[]; // Page-level JSON-LD schema (Service, FAQPage, etc.)
}

/**
 * SEO component for managing meta tags
 * Uses react-helmet-async for SSR compatibility
 * Optimized for local SEO targeting Springfield, Brisbane, Logan, Ipswich
 */
export function SEO({
  title,
  description = siteConfig.description,
  keywords,
  image = "/og-image.png",
  url,
  type = "website",
  noIndex = false,
  location,
  jsonLd,
}: SEOProps) {
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  // Build title with location if provided
  const baseTitle = title
    ? `${title}${location ? ` in ${location}` : ""}`
    : `Mortgage Broker Springfield, Brisbane & Logan`;

  const fullTitle = `${baseTitle} | ${siteConfig.appName}`;

  const fullUrl = url ? `${siteConfig.url}${url}` : siteConfig.url;
  const fullImage = image.startsWith("http") ? image : `${siteConfig.url}${image}`;

  // Use provided keywords or default site keywords
  const metaKeywords = keywords || siteConfig.keywords;

  return (
    <Helmet htmlAttributes={{ lang: "en-AU" }}>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={metaKeywords.join(", ")} />

      {/* Geo Meta Tags for Local SEO */}
      <meta name="geo.region" content="AU-QLD" />
      <meta name="geo.placename" content="Springfield, Queensland" />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={siteConfig.appName} />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Page-level JSON-LD */}
      {jsonLdArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
